import { Button } from "@/components/core/buttons/button";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage, FormPrepend } from "@/components/core/forms/form.tsx";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import courtBranchService from "@/features/court-detail/api/services/court-branches-service";
import { useUserAssignBranchMutation } from "@/features/dashboard/api/queries/user-queries";
import type { UserAssignBranchFormSchema } from "@/features/dashboard/types/user";
import { queryClient } from "@/query-client";
import { useState } from "react";
import { useForm } from "react-hook-form";
import DataGrid, { type ColumnDef } from "../core/data-grid";
import Combobox from "../core/forms/combobox";

export default function SelectBranchModal({
	data,
}: {
	data?: any;
}) {
	const { closeModal } = useModalControl();
	const form = useForm<UserAssignBranchFormSchema>({
		defaultValues: {
			user_id: data.id,
			court_branch: null,
			court_branch_id: "",
		},
	});

	const setBranchMutation = useUserAssignBranchMutation();
	const courtBranch = form.watch("court_branch");

	const branchInformationColumns: ColumnDef<any>[] = [
		{
			id: "name",
			label: "Name",
			cell: ({ data }) => <span>{data?.name || "N/A"}</span>,
		},
		{
			id: "category",
			label: "Category",
			cell: ({ data }) => <span>{data?.category || "N/A"}</span>,
		},
		{
			id: "judge",
			label: "Judge",
			cell: ({ data }) => <span>{data?.judge || "N/A"}</span>,
		},
		{
			id: "address",
			label: "Address",
			cell: ({ data }) => <span>{data?.address || "N/A"}</span>,
		},
	];

	const onSubmit = async (data: any) => {
		console.log(data, "submit");
		await setBranchMutation.mutateAsync({
			data,
		});
		queryClient.invalidateQueries({ queryKey: ["user"] });
		closeModal();
	};

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full rounded-md border border-border/50">
						<div className="flex flex-col items-center justify-between w-full py-3 px-4 gap-3">
							<div className="grid grid-cols-12 w-full py-1 gap-x-4 gap-y-3">
								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="court_branch_id"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Court Branch</FormLabel>
													<FormDescription />
												</div>
												<FormGroup>
													<Combobox
														returnFullOption
														onValueChange={(value) => {
															console.log(value);
															form.setValue("court_branch_id", value?.value);
															form.setValue("court_branch", {
																id: value?.value,
																name: value?.label,
																judge: value?.judge,
																category: value?.category,
																address: value?.address,
															});
														}}
														placeholderValue={{
															label: courtBranch?.name ?? "",
															value: courtBranch?.id ?? "",
														}}
														loadOptions={async (value: string) => {
															const response = await courtBranchService.list({
																"filter[search]": value,
																"filter[court_station_id]": data.court_station_id,
															});

															return response.data.map((branch: any) => ({
																label: branch.formatted_name,
																value: branch.id,
																judge: branch.judge,
																category: branch.category,
																address: branch.address,
															}));
														}}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								{courtBranch && (
									<div className="col-span-12 flex">
										<div className="flex flex-col w-full rounded-md border border-border/50">
											<DataGrid variant="compact" data={courtBranch} columns={branchInformationColumns} />
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-row justify-between w-full pt-4">
					<Button
						className=""
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button className="" loading={form.formState.isSubmitting}>
						Confirm
					</Button>
				</div>
			</form>
		</Form>
	);
}
